/**
 * DevUtilitiesMenu - Menu Content for Dev Utilities
 *
 * Shows region indicator and region-specific utility options.
 */

import { Box, Collapsible, Menu, Separator, Text, VStack } from '@chakra-ui/react';
import { Bug, CheckSquare, ChevronDown, ChevronUp, Copy, FlaskConical } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useDevUtilities } from '~/hooks/useDevUtilities';
import { useProjectStore } from '~/hooks/useProjectStore';
import {
  detectCurrentPage,
  getUtilityConfigForPage,
} from '~/services/dev-utilities/page-detection-service';

interface DevUtilitiesMenuProps {
  isDev: boolean;
  regionName: 'DEV' | 'PRODUCTION';
  projectId?: string;
}

export const DevUtilitiesMenu: React.FC<DevUtilitiesMenuProps> = ({
  isDev,
  regionName,
  projectId: propProjectId,
}) => {
  const location = useLocation();
  const [isDebugOpen, setIsDebugOpen] = useState(false);
  const [hasSelectedCandidate, setHasSelectedCandidate] = useState(false);

  // Use projectId from prop (passed from button) - button already parsed it from URL
  // Don't use useParams here since this component may render outside route context
  const projectId = propProjectId;
  const devUtils = useDevUtilities(projectId);

  // Detect current page and get utility configuration
  const currentPage = detectCurrentPage(location.pathname);
  const pageConfig = getUtilityConfigForPage(currentPage);

  // Get project metadata for conditional rendering
  const project = useProjectStore((s) => (projectId ? s.projects[projectId] : undefined));
  const isTestingProject = project?.metadata?.isTestingProject ?? false;

  // Listen for candidate selection changes from ProposalDevelopmentPage
  useEffect(() => {
    const handleCandidateSelectionChange = (
      event: CustomEvent<{ hasSelectedCandidate: boolean }>
    ) => {
      setHasSelectedCandidate(event.detail.hasSelectedCandidate);
    };

    window.addEventListener(
      'dev-utilities:candidate-selection-changed',
      handleCandidateSelectionChange as EventListener
    );

    // Request current state after setting up listener to handle cases where
    // candidate is already selected when this component mounts
    const requestCurrentState = new CustomEvent('dev-utilities:request-candidate-state');
    window.dispatchEvent(requestCurrentState);

    return () => {
      window.removeEventListener(
        'dev-utilities:candidate-selection-changed',
        handleCandidateSelectionChange as EventListener
      );
    };
  }, []);

  const regionColor = isDev ? 'green.500' : 'red.500';
  const regionBg = isDev ? 'green.50' : 'red.50';

  const handleExistingDebtFill = (template: 'lakeview2016' | 'lakeview2020' | 'lakeview2022') => {
    const templates = devUtils.getExistingDebtTemplates();
    const selectedTemplate = templates[template];

    // Dispatch a custom event that the existing debt component can listen to
    // This allows the dev utilities menu to trigger quick fill from anywhere
    const event = new CustomEvent('dev-utilities:fill-existing-debt', {
      detail: { template: selectedTemplate },
    });
    window.dispatchEvent(event);
  };

  const handleDeveloperAction = (
    action:
      | 'copy-as-testing'
      | 'values-testing'
      | 'debug-inspector'
      | 'copy-cashflow-info'
      | 'sanity-check-cashflows'
  ) => {
    // Dispatch custom events that ProposalDevelopmentPage listens to
    const event = new CustomEvent(`dev-utilities:${action}`);
    window.dispatchEvent(event);
  };

  return (
    <VStack align="stretch" gap={0}>
      {/* Region Indicator Header */}
      <Box
        px={3}
        py={2}
        bg={regionBg}
        borderTopRadius="md"
        borderBottom="1px solid"
        borderColor={regionColor}
      >
        <Text fontSize="xs" fontWeight="bold" color={regionColor} textTransform="uppercase">
          {regionName}
        </Text>
      </Box>

      {/* Debug Section - Collapsible */}
      {/* Note: Debug section is visible in both dev and production for super admin troubleshooting */}
      <Collapsible.Root open={isDebugOpen} onOpenChange={(e) => setIsDebugOpen(e.open)}>
        <Collapsible.Trigger asChild>
          <Box
            px={2}
            py={2}
            bg="gray.50"
            borderBottom="1px solid"
            borderColor="gray.200"
            cursor="pointer"
            _hover={{ bg: 'gray.100' }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
              <Text fontSize="xs" fontWeight="semibold" color="gray.600">
                Debug Info
              </Text>
              {isDebugOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </Box>
          </Box>
        </Collapsible.Trigger>
        <Collapsible.Content>
          <Box px={2} py={2} bg="gray.50" borderBottom="1px solid" borderColor="gray.200">
            <VStack align="stretch" gap={0.5} fontSize="xs" fontFamily="mono">
              <Text color="gray.600">
                projectId:{' '}
                <Text as="span" color={projectId ? 'green.600' : 'red.600'}>
                  {projectId || 'undefined'}
                </Text>
              </Text>
              <Text color="gray.600">
                propProjectId:{' '}
                <Text as="span" color={propProjectId ? 'green.600' : 'red.600'}>
                  {propProjectId || 'undefined'}
                </Text>
              </Text>
              <Text color="gray.600">
                isDev:{' '}
                <Text as="span" color={isDev ? 'green.600' : 'red.600'}>
                  {String(isDev)}
                </Text>
              </Text>
              <Text color="gray.600">
                regionName:{' '}
                <Text as="span" color={regionName === 'DEV' ? 'green.600' : 'red.600'}>
                  {regionName}
                </Text>
              </Text>
              <Text color="gray.600">
                disabled check:{' '}
                <Text as="span" color={!projectId ? 'red.600' : 'green.600'}>
                  {String(!projectId)}
                </Text>
              </Text>
              <Text color="gray.600">
                currentPage:{' '}
                <Text as="span" color="blue.600">
                  {currentPage}
                </Text>
              </Text>
              <Text color="gray.600">
                pageConfig:{' '}
                <Text as="span" color="blue.600">
                  {JSON.stringify({
                    showLakeview: pageConfig.showLakeviewQuickFill,
                    showDebt: pageConfig.showExistingDebtQuickFill,
                    showDevActions: pageConfig.showDeveloperActions,
                  })}
                </Text>
              </Text>
              <Text color="gray.600">
                isTestingProject:{' '}
                <Text as="span" color={isTestingProject ? 'green.600' : 'gray.600'}>
                  {String(isTestingProject)}
                </Text>
              </Text>
            </VStack>
          </Box>
        </Collapsible.Content>
      </Collapsible.Root>

      {/* Utility Options - Available in both dev and production for super admins */}
      {/* Lakeview Quick Fill - Only on project-setup page */}
      {pageConfig.showLakeviewQuickFill && (
        <Box px={2} py={2}>
          <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={2} px={1}>
            Lakeview Quick Fill
          </Text>
          <VStack align="stretch" gap={1}>
            <Menu.Item
              value="fill-dates"
              onClick={(e) => {
                e.preventDefault();
                devUtils.quickFillDates();
              }}
              disabled={!projectId}
            >
              Fill Dates
            </Menu.Item>
            <Menu.Item
              value="fill-tax"
              onClick={(e) => {
                e.preventDefault();
                devUtils.quickFillTaxValues();
              }}
              disabled={!projectId}
            >
              Fill Tax Values
            </Menu.Item>
            <Menu.Item
              value="fill-millage"
              onClick={(e) => {
                e.preventDefault();
                devUtils.quickFillMillage();
              }}
              disabled={!projectId}
            >
              Fill Millage
            </Menu.Item>
            <Menu.Item
              value="fill-drf"
              onClick={(e) => {
                e.preventDefault();
                devUtils.quickFillDRF();
              }}
              disabled={!projectId}
            >
              Fill DRF
            </Menu.Item>
          </VStack>
        </Box>
      )}

      {/* Separator only if we have both sections */}
      {pageConfig.showLakeviewQuickFill && pageConfig.showExistingDebtQuickFill && <Separator />}

      {/* Existing Debt Quick Fill - Only on existing-debt page */}
      {pageConfig.showExistingDebtQuickFill && (
        <Box px={2} py={2}>
          <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={2} px={1}>
            Existing Debt Templates
          </Text>
          <VStack align="stretch" gap={1}>
            <Menu.Item
              value="fill-debt-2016"
              onClick={() => handleExistingDebtFill('lakeview2016')}
              disabled={!projectId}
            >
              Fill Existing Debt 2016
            </Menu.Item>
            <Menu.Item
              value="fill-debt-2020"
              onClick={() => handleExistingDebtFill('lakeview2020')}
              disabled={!projectId}
            >
              Fill Existing Debt 2020
            </Menu.Item>
            <Menu.Item
              value="fill-debt-2022"
              onClick={() => handleExistingDebtFill('lakeview2022')}
              disabled={!projectId}
            >
              Fill Existing Debt 2022
            </Menu.Item>
          </VStack>
        </Box>
      )}

      {/* Separator before developer actions if we have quick fill options */}
      {(pageConfig.showLakeviewQuickFill || pageConfig.showExistingDebtQuickFill) &&
        pageConfig.showDeveloperActions && <Separator />}

      {/* Developer Actions - Only on proposal page */}
      {pageConfig.showDeveloperActions && (
        <Box px={2} py={2}>
          <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={2} px={1}>
            Developer Actions
          </Text>
          <VStack align="stretch" gap={1}>
            {/* Copy as Testing - only for non-testing projects */}
            {!isTestingProject && (
              <Menu.Item
                value="copy-as-testing"
                onClick={() => handleDeveloperAction('copy-as-testing')}
                disabled={!projectId}
              >
                <Copy size={14} /> Copy as Testing
              </Menu.Item>
            )}
            {/* Values Testing - only for testing projects */}
            {isTestingProject && (
              <Menu.Item
                value="values-testing"
                onClick={() => handleDeveloperAction('values-testing')}
                disabled={!projectId}
              >
                <FlaskConical size={14} /> Values Testing
              </Menu.Item>
            )}
            {/* Debug Inspector - always available on proposal page */}
            <Menu.Item
              value="debug-inspector"
              onClick={() => handleDeveloperAction('debug-inspector')}
              disabled={!projectId}
            >
              <Bug size={14} /> Debug Inspector
            </Menu.Item>
            {/* Copy Cashflow Information - only when candidate is selected */}
            <Menu.Item
              value="copy-cashflow-info"
              onClick={() => handleDeveloperAction('copy-cashflow-info')}
              disabled={!projectId || !hasSelectedCandidate}
            >
              <Copy size={14} /> Copy Cashflow Information
            </Menu.Item>
            {/* Sanity Check Cashflows - only when candidate is selected */}
            <Menu.Item
              value="sanity-check-cashflows"
              onClick={() => handleDeveloperAction('sanity-check-cashflows')}
              disabled={!projectId || !hasSelectedCandidate}
            >
              <CheckSquare size={14} /> Sanity Check Cashflows
            </Menu.Item>
          </VStack>
        </Box>
      )}

      {/* Show message if no utilities available for this page */}
      {!pageConfig.showLakeviewQuickFill &&
        !pageConfig.showExistingDebtQuickFill &&
        !pageConfig.showDeveloperActions && (
          <Box px={2} py={4}>
            <Text fontSize="sm" color="gray.500" textAlign="center">
              No utilities available for {pageConfig.displayName}
            </Text>
          </Box>
        )}
    </VStack>
  );
};
